import React, { useEffect, useRef, useState } from "react";
import { useCarrito } from "../context/CarritoContext";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

export default function Cotizar() {
  const { carrito, vaciarCarrito } = useCarrito();
  const navigate = useNavigate();

  // 🔵 ancla para scrollear con offset (si tienes header fijo)
  const topRef = useRef(null);

  // 🔵 para leer/limpiar la query y el state que envía el botón "Cotizar ahora"
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [enviando, setEnviando] = useState(false);
  const [formData, setFormData] = useState({
    empresa: "",
    nombre: "",
    apellido: "",
    telefono: "",
    nit: "",
    correo: "",
    direccion: "",
  });

  
  // Si no hay carrito y no estamos enviando, regresar al inicio
  useEffect(() => {
    if (!enviando && carrito.length === 0) {
      navigate("/");
    }
  }, [carrito, enviando, navigate]);

  useEffect(() => {
    const prev = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = prev || "auto";
      }
    };
  }, []);

  // 🔵 Efecto: subir arriba cuando venimos desde "Cotizar ahora" o hash
  useEffect(() => {
    const hasScrollParam = searchParams.has("scroll");
    const fromState = Boolean(location.state?.forceScroll);
    const toHashTop = location.hash === "#top" || location.hash === "#cotizar-top";
  
    if (!(hasScrollParam || fromState || toHashTop)) return;
  
    let attempts = 0;
  
    const cleanSignals = () => {
      if (hasScrollParam) {
        const updated = new URLSearchParams(searchParams);
        updated.delete("scroll");
        setSearchParams(updated, { replace: true });
      }
      if (fromState) {
        navigate(".", { replace: true, state: {} });
      }
      if (toHashTop) {
        navigate(location.pathname + location.search, { replace: true });
      }
    };
  
    const step = () => {
      const yTarget = topRef.current
        ? topRef.current.getBoundingClientRect().top + window.scrollY - 300
        : 0;
  
      // 1) Rompe estar “pegado” al fondo
      window.scrollTo({ top: yTarget, behavior: attempts === 0 ? "auto" : "smooth" });
  
      attempts += 1;
      if (attempts < 10) {
        requestAnimationFrame(step);
      } else {
        setTimeout(cleanSignals, 120);
      }
    };
  
    requestAnimationFrame(step);
  }, [location.hash, location.state, navigate, searchParams, setSearchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const enviarCotizacion = async () => {
    setEnviando(true);
    const requeridos = ["nombre", "telefono", "correo", "direccion"];
    for (let campo of requeridos) {
      if (!formData[campo]) {
        toast.error("Por favor complete los campos obligatorios (*)");
        setEnviando(false);
        return;
      }
    }

    const payload = {
      ...formData,
      productos: carrito,
    };

    try {
      const res = await fetch("/.netlify/functions/send-quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok) {
        toast.success("Cotización enviada por correo");
        const resumen = [...carrito];
        sessionStorage.setItem("resumenPedido", JSON.stringify(resumen));
        vaciarCarrito();
        navigate("/success", { state: { resumen } });
      } else {
        toast.error(result.message || "Error al enviar correo");
        setEnviando(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error de red al enviar correo");
      setEnviando(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* 🔵 ancla para el scroll con offset */}
      <div ref={topRef} id="cotizar-top" className="scroll-mt-24"></div>

      <h1 className="text-2xl font-bold text-sky-800 mb-4">Solicitar Cotización</h1>
      <p className="text-sm text-gray-600 mb-6">
        Complete los datos para recibir su cotización por correo electrónico
      </p>

      <div className="space-y-4">
        {[
          ["empresa", "Empresa", "Ej: Panadería La Estrella"],
          ["nombre", "*Nombre", "Ej: Andrea"],
          ["apellido", "Apellido", "Ej: González"],
          ["telefono", "*Teléfono", "Ej: 5555-1234"],
          ["nit", "NIT", "Ej: 1234567-8 o C/F"],
          ["correo", "*Correo", "Ej: contacto@empresa.com"],
          ["direccion", "*Dirección de entrega", "Ej: 5a. Avenida 3-45 zona 1"],
        ].map(([name, label, placeholder]) => (
          <div key={name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
              type={name === "correo" ? "email" : "text"}
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              placeholder={placeholder}
              required={label.startsWith("*")}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder-gray-400"
            />
          </div>
        ))}
      </div>

      {/* Resumen de pedido */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold text-sky-700 mb-2">Resumen del pedido:</h2>
        <ul className="divide-y divide-gray-200 text-sm">
          {carrito.map((item) => (
            <li key={item.sku} className="py-2 flex justify-between items-start">
              <div>
                <span className="font-medium">{item.nombre}</span>
                {item.modoVenta && (
                  <span className="text-xs text-gray-500 ml-1">({item.modoVenta})</span>
                )}
              </div>
              <span className="text-gray-600">x{item.cantidad}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Botones */}
      <div className="mt-6 flex justify-end gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          Cancelar
        </button>
        <button
          onClick={enviarCotizacion}
          disabled={enviando}
          className={`relative overflow-hidden px-6 py-2 rounded-md font-medium transition text-white
            ${enviando ? "bg-empresarialDark" : "bg-empresarial hover:bg-empresarialDark"} 
            ${enviando ? "btn-progress animating" : ""}
          `}
        >
          <span className="relative z-10">
            {enviando ? "Enviando cotización..." : "Enviar Cotización"}
          </span>
        </button>
      </div>
    </div>
  );
}
